import { createApi } from '@reduxjs/toolkit/query/react';
import type { PermissionsType, RolesType } from '../../types';
import { baseQueryWithReauth } from '../utils/prepHeaders';

export type args = {
  name?: string;
  permissions?: PermissionsType.PermissionsPartial;
};

export type responses = {};

export const rolesApi = createApi({
  reducerPath: 'rolesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Role'],
  endpoints: (builder) => ({
    getAllRoles: builder.query<RolesType.Role[], void>({
      query: () => ({
        url: '/user/roles/',
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor: 300,
      providesTags: [{ type: 'Role', id: 'all' }],
    }),

    getRoleById: builder.query<RolesType.Role, number>({
      query: (roleId) => ({
        url: `/user/roles/${roleId}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor: 300,
      providesTags: (result, error, roleId) =>
        result ? [{ type: 'Role', id: roleId }] : [],
    }),

    postRole: builder.mutation<
      any,
      {
        name: string;
        permissions: string[];
      }
    >({
      query: (role) => ({
        url: '/user/roles/',
        method: 'POST',
        body: role,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: ['Role', { type: 'Role', id: 'all' }],
    }),

    patchRole: builder.mutation<RolesType.Role, { roleId: number; args: args }>(
      {
        query: ({ roleId, args }) => ({
          url: `/user/roles/${roleId}/`,
          method: 'PATCH',
          body: args,
          validateStatus: (response, result) =>
            response.status === 200 && !result.isError,
        }),
        invalidatesTags: (result, error, { roleId }) => [
          'Role',
          { type: 'Role', id: 'all' },
          { type: 'Role', id: roleId },
        ],
      },
    ),

    deleteRole: builder.mutation<any, number>({
      query: (roleId) => ({
        url: `/user/roles/${roleId}/`,
        method: 'DELETE',
        validateStatus: (response, result) => response.status === 204,
      }),
      invalidatesTags: (result, error, roleId) => [
        'Role',
        { type: 'Role', id: 'all' },
        { type: 'Role', id: roleId },
      ],
    }),
  }),
});

export const {
  useGetAllRolesQuery,
  useGetRoleByIdQuery,
  usePostRoleMutation,
  usePatchRoleMutation,
  useDeleteRoleMutation,
} = rolesApi;

export namespace RolesApi {
  export const Api = rolesApi;
  export type Args = args;
  export type Responses = responses;
}
